import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CircleAlertIcon,
  CircleCheckIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  MessageSquareXIcon,
  EyeIcon,
  EyeOffIcon,
  ShieldAlertIcon,
  Trash2Icon,
  type LucideIcon,
} from 'lucide-react';
import { app, comments, notes, type BackedUpComment, type NoteAction, type NoteDetail } from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { useNoteActions } from '@/lib/useNoteActions';
import { PageShell } from '@/components/PageShell';
import { RowList } from '@/components/RowList';
import { SectionHeader } from '@/components/SectionHeader';
import { InfoCard, TaskProgressCard } from '@/components/task-ui';
import { PageSkeleton } from '@/components/skeletons';
import { Card } from '@/components/ui/card';
import { CARD, FOOTNOTE } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * 薯薯全能通 · 笔记详情。
 *
 * **重点是操作，不是展示**：笔记信息只放一张小卡，够认出「是哪一篇」；主体是操作列表
 * （原生 `notes.actions` 下发，平台差异由原生决定）和这篇的操作记录。
 */

const ICONS: Record<string, LucideIcon> = {
  hide: EyeOffIcon,
  unhide: EyeIcon,
  delete: Trash2Icon,
  clearComments: MessageSquareXIcon,
};

const VISIBILITY: Record<string, string> = {
  public: '公开',
  private: '仅自己可见',
  friends: '好友可见',
  reviewing: '审核中',
};

function formatDate(ms: number | null): string | null {
  if (!ms) return null;
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
}

/** 已备份评论最多预览这么多条 */
const COMMENT_PREVIEW = 30;

function formatTime(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ShushuNoteDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { info, loading: infoLoading, isNative } = useAppInfo();
  const { selectedId: accountId, loading: accountsLoading, error: accountError } = useAccounts();
  const [note, setNote] = useState<NoteDetail | null>(null);
  const [actions, setActions] = useState<NoteAction[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  /** 本机备份的评论（「评论备份」存的；旧版 App 没有这个方法时为空） */
  const [savedComments, setSavedComments] = useState<BackedUpComment[]>([]);
  useGroupedSurface();

  const loaded = !infoLoading && !accountsLoading && (note !== null || loadError !== null || !accountId);
  useAppReady(loaded);

  useEffect(() => {
    if (!accountId || !id) return;
    let cancelled = false;
    Promise.all([
      notes.get(accountId, id),
      notes.actions(accountId, [id]),
      comments.list(accountId, id).catch(() => ({ items: [] as BackedUpComment[] })),
    ])
      .then(([detail, catalog, saved]) => {
        if (cancelled) return;
        setNote(detail);
        setActions(catalog.actions);
        setSavedComments(saved.items);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(describeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, id, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const { snapshot, running, error: runError, run } = useNoteActions(accountId, reload);

  const applicable = (actions ?? []).filter((a) => a.applicable > 0);
  const regular = applicable.filter((a) => !a.destructive);
  // 删除笔记放最后（最重的操作在最下面）
  const destructive = applicable
    .filter((a) => a.destructive)
    .sort((a, b) => Number(a.key === 'delete') - Number(b.key === 'delete'));
  const unit = snapshot?.action === 'clearComments' ? '条' : '篇';
  // 整账号批量入口：平台支持哪个就给哪个（隐藏 / 公开 / 删除），删除放最后
  const accountBatch = (
    [
      { key: 'hide', label: '批量隐藏全部…', title: '全部设为仅自己可见' },
      { key: 'unhide', label: '批量公开全部…', title: '全部设为公开' },
      { key: 'delete', label: '批量删除全部…', title: '删除全部' },
    ] as const
  ).filter((entry) => (actions ?? []).some((a) => a.key === entry.key));
  const unsupported = actions !== null && actions.length === 0;
  const runningLabel = (actions ?? []).find((a) => a.key === snapshot?.action)?.label;

  const actionRow = (action: NoteAction) => {
    const Icon = ICONS[action.key] ?? CircleAlertIcon;
    const disabled = running || !!action.disabledReason;
    return (
      <button
        key={action.key}
        type="button"
        data-haptic
        disabled={disabled}
        onClick={() => void run(action, [id])}
        className="flex w-full items-center gap-3 py-3 text-left disabled:opacity-50 active:opacity-60"
      >
        <span className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-full">
          <Icon className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-body">{action.label}</span>
          <span className="text-muted-foreground block text-footnote">{action.disabledReason ?? action.detail}</span>
        </span>
      </button>
    );
  };

  return (
    <PageShell title="笔记详情" description="认出是哪一篇，然后对它操作">
      {!isNative && loaded ? (
        <Card className={CARD}>
          <p className="text-muted-foreground text-xs">
            当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
          </p>
        </Card>
      ) : null}

      {note ? (
        <>
          {/* 识别卡：只用来认出是哪一篇 */}
          <Card className={CARD}>
            <div className="flex items-center gap-3">
              {note.cover ? (
                <img
                  src={note.cover}
                  alt=""
                  referrerPolicy="no-referrer"
                  className={cn('bg-muted size-14 shrink-0 rounded-lg object-cover', note.deleted && 'opacity-50')}
                  onError={(e) => {
                    e.currentTarget.style.visibility = 'hidden';
                  }}
                />
              ) : (
                <div className="bg-muted size-14 shrink-0 rounded-lg" />
              )}
              <div className="min-w-0 flex-1">
                <p className={cn('line-clamp-2 text-body font-semibold', note.deleted && 'text-muted-foreground')}>
                  {note.title || '无标题'}
                </p>
                <p className="text-muted-foreground mt-0.5 truncate text-footnote">
                  {[
                    formatDate(note.publishedAt),
                    note.deleted ? '已从平台删除' : note.visibility ? VISIBILITY[note.visibility] : null,
                    note.backedUp ? '已备份' : '未备份',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>
          </Card>

          {running && snapshot ? (
            <TaskProgressCard
              title={runningLabel ? `正在${runningLabel}` : '正在处理'}
              done={snapshot.done}
              total={snapshot.total}
              detail={
                snapshot.action === 'clearComments'
                  ? `已删除 ${snapshot.done ?? 0} 条${snapshot.failed ? ` · 失败 ${snapshot.failed} 条` : ''} · 每条间隔约 2.5 秒`
                  : snapshot.failed
                    ? `失败 ${snapshot.failed} ${unit}`
                    : undefined
              }
            />
          ) : null}
          {snapshot?.state === 'completed' && snapshot.note ? (
            <InfoCard title="已停止" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
              {snapshot.note}
            </InfoCard>
          ) : null}
          {snapshot?.state === 'failed' && snapshot.error ? (
            <InfoCard title="操作失败" icon={<ShieldAlertIcon className="text-destructive size-5 shrink-0" />}>
              {describeError(snapshot.error)}
            </InfoCard>
          ) : null}

          {note.deleted ? (
            <InfoCard title="已从平台删除" icon={<Trash2Icon className="text-muted-foreground size-5 shrink-0" />}>
              {note.backedUp ? '本机备份已保留，可以在迁移里重新发布到其他账号。' : '这篇删除前没有备份，本机只保留了标题等信息。'}
            </InfoCard>
          ) : null}

          {unsupported && !note.deleted ? (
            <InfoCard title="暂不支持操作" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
              该平台还不支持对笔记操作，敬请期待。
            </InfoCard>
          ) : null}

          {regular.length > 0 || (note.url && !note.deleted) ? (
            <Card className={CARD}>
              <SectionHeader title="操作" />
              <RowList>
                {regular.map(actionRow)}
                {note.url && !note.deleted ? (
                  <button
                    type="button"
                    data-haptic
                    onClick={() => void app.openUrl(note.url!)}
                    className="flex w-full items-center gap-3 py-3 text-left active:opacity-60"
                  >
                    <span className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-full">
                      <ExternalLinkIcon className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-body">在平台上查看</span>
                      <span className="text-muted-foreground block text-footnote">仅自己可见的内容需在平台 App 内查看</span>
                    </span>
                  </button>
                ) : null}
              </RowList>
            </Card>
          ) : null}

          {/* 破坏性操作单独一张卡、放最后（iOS 惯例） */}
          {destructive.map((action) => (
            <div key={action.key} className="flex flex-col gap-4">
              <Card className={cn(CARD, 'py-0')}>
                <button
                  type="button"
                  data-haptic
                  disabled={running || !!action.disabledReason}
                  onClick={() => void run(action, [id])}
                  className="text-destructive w-full py-3.5 text-center text-body font-medium disabled:opacity-50 active:opacity-60"
                >
                  {action.label}
                </button>
              </Card>
              <p className={FOOTNOTE}>{action.disabledReason ?? action.detail}</p>
            </div>
          ))}

          {/* 整个账号的批量操作（对照搬小书「账号整理」：批量隐藏 / 显示 / 删除），推一个原生页；v5 起 */}
          {(info?.bridgeVersion ?? 0) >= 5 && accountBatch.length > 0 ? (
            <div className="flex flex-col gap-4">
              <Card className={CARD}>
                <SectionHeader title="整个账号" />
                <RowList>
                  {accountBatch.map((entry) => (
                    <button
                      key={entry.key}
                      type="button"
                      data-haptic
                      disabled={running}
                      onClick={async () => {
                        const path = `/shushu/batch?accountId=${encodeURIComponent(accountId)}&action=${entry.key}`;
                        if (!(await app.openPage(path, entry.title))) navigate(path);
                      }}
                      className={cn(
                        'flex w-full items-center justify-between py-3 text-left text-body disabled:opacity-50 active:opacity-60',
                        entry.key === 'delete' && 'text-destructive',
                      )}
                    >
                      <span>{entry.label}</span>
                      <ChevronRightIcon className="text-muted-foreground size-4" />
                    </button>
                  ))}
                </RowList>
              </Card>
              <p className={FOOTNOTE}>逐页读取这个账号的线上全部内容执行，包括本机还没同步到的；已是目标状态的跳过。</p>
            </div>
          ) : null}

          {savedComments.length > 0 ? (
            <div className="flex flex-col gap-4">
              <Card className={CARD}>
                <SectionHeader title="已备份评论" trailing={`${savedComments.length} 条`} />
                <RowList>
                  {savedComments.slice(0, COMMENT_PREVIEW).map((comment) => (
                    <div key={comment.id} className="flex flex-col gap-0.5 py-3">
                      <p className="text-subheadline font-medium">{comment.author}</p>
                      <p className="text-subheadline leading-snug break-words whitespace-pre-wrap">{comment.text}</p>
                      <p className="text-muted-foreground text-footnote tabular-nums">
                        {[
                          comment.createdAt ? formatTime(comment.createdAt) : null,
                          comment.likeCount ? `赞 ${comment.likeCount}` : null,
                          comment.replyCount ? `${comment.replyCount} 条回复` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  ))}
                </RowList>
              </Card>
              <p className={FOOTNOTE}>
                {savedComments.length > COMMENT_PREVIEW ? `只显示最新 ${COMMENT_PREVIEW} 条；` : ''}
                备份于 {formatTime(savedComments[0].fetchedAt)}，只含一级评论。导出笔记时会一起带上。
              </p>
            </div>
          ) : null}

          {note.history.length > 0 ? (
            <Card className={CARD}>
              <SectionHeader title="操作记录" />
              <RowList>
                {note.history.map((entry, index) => (
                  <div key={`${entry.taskId}-${index}`} className="flex items-start gap-3 py-3">
                    {entry.status === 'failed' ? (
                      <CircleAlertIcon className="text-destructive mt-0.5 size-4 shrink-0" />
                    ) : (
                      <CircleCheckIcon
                        className={cn('mt-0.5 size-4 shrink-0', entry.status === 'done' ? 'text-foreground' : 'text-muted-foreground')}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-subheadline">
                        {entry.name}
                        <span className="text-muted-foreground">
                          {entry.status === 'failed' ? ' · 失败' : entry.status === 'pending' ? ' · 等待中' : ''}
                        </span>
                      </p>
                      {entry.error ? <p className="text-destructive text-footnote">{describeError(entry.error)}</p> : null}
                    </div>
                    <span className="text-muted-foreground shrink-0 text-footnote tabular-nums">{formatTime(entry.at)}</span>
                  </div>
                ))}
              </RowList>
            </Card>
          ) : null}
        </>
      ) : null}

      {(loadError ?? runError ?? accountError) ? (
        <p className="text-destructive px-1 text-sm">{loadError ?? runError ?? accountError}</p>
      ) : null}

      {!loaded ? <PageSkeleton kind="note" /> : null}
    </PageShell>
  );
}
